"use client";

import { collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { auth, db } from "./firebase";

export type PartnerJob = {
  id: string;
  problem: string;
  vehicle: string;
  locationLabel: string;
  price: number;
  status: string;
  customerId: string;
  atMs: number;
};

export type EarningsSummary = {
  completedCount: number;
  cancelledCount: number;
  totalEarned: number;
  todayEarned: number;
  todayCount: number;
  monthEarned: number;
  monthCount: number;
  averageTicket: number;
};

function millis(value: unknown) {
  return (value as { toMillis?: () => number } | undefined)?.toMillis?.() || 0;
}

/** Every request ever assigned to the signed-in partner, newest first. */
export function watchMyJobHistory(callback: (jobs: PartnerJob[]) => void) {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    callback([]);
    return () => undefined;
  }
  const jobsQuery = query(
    collection(db, "serviceRequests"),
    where("partnerId", "==", uid),
    orderBy("updatedAt", "desc"),
    limit(200),
  );
  return onSnapshot(jobsQuery, (snapshot) => {
    callback(snapshot.docs.map((entry) => {
      const data = entry.data();
      return {
        id: entry.id,
        problem: String(data.problem || "Job"),
        vehicle: String(data.vehicle || ""),
        locationLabel: String(data.locationLabel || ""),
        price: Number(data.price || 0),
        status: String(data.status || ""),
        customerId: String(data.customerId || ""),
        atMs: millis(data.completedAt) || millis(data.updatedAt) || millis(data.createdAt),
      } satisfies PartnerJob;
    }));
  }, () => callback([]));
}

export function summarizeEarnings(jobs: PartnerJob[], now = new Date()): EarningsSummary {
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

  const completed = jobs.filter((job) => job.status === "completed");
  const totalEarned = completed.reduce((sum, job) => sum + job.price, 0);
  const today = completed.filter((job) => job.atMs >= startOfDay);
  const month = completed.filter((job) => job.atMs >= startOfMonth);

  return {
    completedCount: completed.length,
    cancelledCount: jobs.filter((job) => job.status === "cancelled").length,
    totalEarned,
    todayEarned: today.reduce((sum, job) => sum + job.price, 0),
    todayCount: today.length,
    monthEarned: month.reduce((sum, job) => sum + job.price, 0),
    monthCount: month.length,
    averageTicket: completed.length ? Math.round(totalEarned / completed.length) : 0,
  };
}

export type CommissionDue = {
  id: string;
  partnerId: string;
  requestId: string;
  jobAmount: number;
  amount: number;
  status: string;
  atMs: number;
};

export function watchMyCommissions(callback: (rows: CommissionDue[]) => void) {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    callback([]);
    return () => undefined;
  }
  const duesQuery = query(
    collection(db, "commissionDues"),
    where("partnerId", "==", uid),
    limit(100),
  );
  return onSnapshot(duesQuery, (snapshot) => {
    const rows = snapshot.docs.map((entry) => {
      const data = entry.data();
      return {
        id: entry.id,
        partnerId: String(data.partnerId || ""),
        requestId: String(data.requestId || entry.id),
        jobAmount: Number(data.jobAmount || 0),
        amount: Number(data.amount || 0),
        status: String(data.status || "due"),
        atMs: millis(data.createdAt) || millis(data.updatedAt),
      } satisfies CommissionDue;
    });
    rows.sort((left, right) => right.atMs - left.atMs);
    callback(rows);
  }, () => callback([]));
}

export async function claimCommissionPaid(dueId: string) {
  await updateDoc(doc(db, "commissionDues", dueId), {
    status: "claimed",
    claimedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
